import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  FieldErrors,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ProfileFormValues } from "./profileFormTypes";
import { User, MapPin, FileText } from "lucide-react";
import { Input } from "@/components/ui";
import { AddressFields } from "./AddressFields";
import { DocumentFields } from "./DocumentFields";
import { useTaxIdentifierHint } from "./useTaxIdentifierHint";
import type { ProfileSectionControls } from "./profileSectionTypes";
import { ProfileSectionCard } from "./ProfileSectionCard";

interface PersonFormSectionProps {
  register: UseFormRegister<ProfileFormValues>;
  setValue: UseFormSetValue<ProfileFormValues>;
  watch: UseFormWatch<ProfileFormValues>;
  errors: FieldErrors<ProfileFormValues>;
  profileSyncKey: string;
  registeredAddressSyncKey: string;
  sections: {
    personal: ProfileSectionControls;
    residence: ProfileSectionControls;
    identity: ProfileSectionControls;
  };
}

export function PersonFormSection({
  register,
  setValue,
  watch,
  errors,
  registeredAddressSyncKey,
  sections,
}: PersonFormSectionProps) {
  const { t } = useTranslation("settings");
  const fiscalCode = watch("fiscal_code");
  const fiscalLookup = useTaxIdentifierHint(fiscalCode, "fiscal_code");

  return (
    <>
      <ProfileSectionCard
        title={t("forms.person.cardPersonal")}
        icon={User}
        {...sections.personal}
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Input
            {...register("first_name")}
            label={t("forms.person.firstName")}
            required
            placeholder={t("forms.person.idAsShown")}
            error={errors.first_name?.message}
          />
          <Input
            {...register("last_name")}
            label={t("forms.person.lastName")}
            required
            placeholder={t("forms.person.idAsShown")}
            error={errors.last_name?.message}
          />
          <Input
            {...register("birth_date")}
            label={t("forms.person.birthDate")}
            required
            type="date"
            error={errors.birth_date?.message}
          />
          <Input
            {...register("birth_place")}
            label={t("forms.person.birthPlace")}
            required
            placeholder={t("forms.person.birthPlacePh")}
            error={errors.birth_place?.message}
          />
          <Input
            {...register("fiscal_code")}
            label={t("forms.person.fiscalCode")}
            required
            placeholder={t("forms.person.fiscalCodePh")}
            error={errors.fiscal_code?.message}
            hint={fiscalLookup.hint}
            rightIcon={fiscalLookup.rightIcon}
            className="uppercase"
          />
        </div>
      </ProfileSectionCard>

      <ProfileSectionCard
        title={t("forms.person.cardResidence")}
        icon={MapPin}
        {...sections.residence}
      >
        <AddressFields
          register={register}
          setValue={setValue}
          watch={watch}
          errors={errors}
          syncKey={registeredAddressSyncKey}
        />
      </ProfileSectionCard>

      <ProfileSectionCard
        title={t("forms.person.cardIdentity")}
        icon={FileText}
        {...sections.identity}
      >
        <DocumentFields register={register} errors={errors} />
      </ProfileSectionCard>
    </>
  );
}
